import React, { memo, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import ApiService from '../../services/ApiService';
import { authActions } from '../../redux/auth';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PreLoader from "../PreLoader";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { StripeConnectedAccountSchema } from '../../Schemas';
import { loadStripe } from "@stripe/stripe-js";
import { decrypt } from "../../utils/crypto";
import {
    CountrySelect,
    StateSelect,
    CitySelect,
} from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";


function Withdrawal() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userId, userData } = useSelector(state => state.auth);

    const [isLoading, setIsLoading] = useState(false);
    const [stripeStatus, setStripeStatus] = useState(null);
    const [amount, setAmount] = useState('');

    const [countryid, setCountryid] = useState(233); // Default to US (id: 233)
    const [stateid, setStateid] = useState(0);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        trigger,
    } = useForm({
        resolver: yupResolver(StripeConnectedAccountSchema),
        mode: 'all',
        defaultValues: {
            individual: {
                email: userData?.email || '',
                phone: '',
                first_name: '',
                last_name: '',
                dob: { day: '', month: '', year: '' },
                address: { line1: '', city: '', state: '', postal_code: '', country: 'US' },
                ssn_last_4: ''
            },
            account_holder_name: '',
            routing_number: '',
            account_number: '',
            confirm_account_number: ''
        }
    });

    const getUserDetails = useCallback(async () => {
        try {
            const response = await new ApiService().POST("/user-info", { userid: userId });
            if (response?.data?.user_details?.[0]) {
                const resData = response.data.user_details[0];
                dispatch(authActions.UpdateUserData(resData));
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    }, [userId, dispatch]);

    const checkStatus = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await new ApiService().GET(`/stripe-account-status?user_id=${userId}`);
            console.log("Stripe account status web API response: ", response.data);
            if (response.data && response.data.status === 1) {
                setStripeStatus(response.data);
            } else {
                setStripeStatus(response.data);
            }
        } catch (error) {
            console.error("Error fetching Stripe status:", error);
            toast.error("Failed to fetch Stripe status.");
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        checkStatus();
        getUserDetails();
    }, [checkStatus, getUserDetails]);

    const handleSetupAccount = async (formData) => {
        setIsLoading(true);
        try {
            // Retrieve and decrypt Stripe Publishable Key
            const keyResponse = await new ApiService().GET(`/get-credential-keys/stripe`);
            if (keyResponse.data.status !== '1') {
                toast.error("Failed to retrieve Stripe credentials.");
                setIsLoading(false);
                return;
            }
            const stripePublishKey = decrypt(keyResponse.data.client_id, process.env.REACT_APP_CRYPTO_KEY);
            const stripe = await loadStripe(stripePublishKey);

            if (!stripe) {
                toast.error("Stripe failed to initialize.");
                setIsLoading(false);
                return;
            }

            const tokenResult = await stripe.createToken('bank_account', {
                country: 'US',
                currency: 'usd',
                routing_number: formData.routing_number?.trim(),
                account_number: formData.account_number?.trim(),
                account_holder_name: formData.account_holder_name?.trim(),
                account_holder_type: 'individual',
            });

            if (tokenResult.error) {
                toast.error(tokenResult.error.message);
                setIsLoading(false);
                return;
            }

            const external_token = tokenResult.token.id;

            // Sanitize phone number (remove non-digits, slice leading 1 if 11 digits)
            const cleanedPhone = formData.individual?.phone?.replace(/\D/g, '') || '';
            const finalPhone = cleanedPhone.length === 11 && cleanedPhone.startsWith('1') ? cleanedPhone.slice(1) : cleanedPhone;

            const payload = {
                user_id: userId,
                external_token: external_token,
                individual: {
                    first_name: formData.individual?.first_name?.trim(),
                    last_name: formData.individual?.last_name?.trim(),
                    email: formData.individual?.email?.trim(),
                    phone: finalPhone,
                    ssn_last_4: formData.individual?.ssn_last_4?.trim(),
                    dob: {
                        day: parseInt(formData.individual?.dob?.day, 10),
                        month: parseInt(formData.individual?.dob?.month, 10),
                        year: parseInt(formData.individual?.dob?.year, 10)
                    },
                    address: {
                        line1: formData.individual?.address?.line1?.trim(),
                        city: formData.individual?.address?.city?.trim(),
                        state: formData.individual?.address?.state?.trim(),
                        postal_code: formData.individual?.address?.postal_code?.trim(),
                        country: formData.individual?.address?.country?.trim() || 'US'
                    }
                }
            };


            const response = await new ApiService().POST("/create-stripe-external-account", payload);

            if (response.data && response.data.status === "1") {
                console.log("Stripe external account created successfully: ", response.data);
                await checkStatus();
            } else {
                setIsLoading(false);
                toast.error(response.data?.message || 'Failed to create Stripe account.');
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error creating Stripe account:", error);
            toast.error("An error occurred during Stripe account creation.");
        }
    };

    const handleWithdrawalSubmit = async (e) => {
        e.preventDefault();

        if (!stripeStatus?.isConnected) {
            toast.error("Please link your bank account first.");
            return;
        }

        if (!stripeStatus?.isPayoutEnabled) {
            toast.error("Payout is not enabled on your account. Please complete bank setup.");
            return;
        }

        const withdrawAmount = parseFloat(amount);
        if (isNaN(withdrawAmount) || withdrawAmount <= 0) {
            toast.error("Please enter a valid amount.");
            return;
        }

        const earnBalance = parseFloat(userData.earn_amount || 0);
        if (withdrawAmount > earnBalance) {
            toast.error("Insufficient balance.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await new ApiService().POST("/user-withdrawal", {
                user_id: userId,
                amount: amount,
                currency: 'USD'
            });
            setIsLoading(false);
            if (response.data && response.data.status === "1") {
                Swal.fire({
                    title: 'Success!',
                    text: 'Withdrawal request submitted successfully.',
                    icon: 'success',
                    confirmButtonColor: '#8ABB45'
                });
                setAmount('');
                getUserDetails(); // Refresh user balance in redux
            } else {
                toast.error(response.data?.message || 'Withdrawal failed.');
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error submitting withdrawal:", error);
            toast.error("An error occurred during withdrawal processing.");
        }
    };

    return (
        <>
            <PreLoader Loader={isLoading} />

            <div className="add-fund">
                <h2>Withdrawable Earned Balance</h2>
                <ul className="a-f-m">
                    <li>${Number(userData.earn_amount || 0).toFixed(2)}</li>
                </ul>

                <div className="card mt-4 p-4 shadow-sm border-0" style={{ background: "rgba(255, 255, 255, 0.08)", backdropFilter: "blur(10px)", borderRadius: "16px", color: "inherit" }}>
                    <h3 className="mb-3 text-success fw-bold">Account Verification</h3>
                    <div className="d-flex justify-content-between mb-2">
                        <span className="opacity-75">Connected to Stripe:</span>
                        <span className={`fw-bold ${stripeStatus?.isConnected ? 'text-success' : 'text-danger'}`}>
                            {stripeStatus?.isConnected ? 'Verified' : 'Not Connected'}
                        </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                        <span className="opacity-75">Payouts Ready:</span>
                        <span className={`fw-bold ${stripeStatus?.isPayoutEnabled ? 'text-success' : 'text-danger'}`}>
                            {stripeStatus?.isPayoutEnabled ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                </div>

                {(stripeStatus?.isConnected && stripeStatus?.isPayoutEnabled && stripeStatus?.isWithDrawable) ? (
                    <form onSubmit={handleWithdrawalSubmit} className="mt-4">
                        <div className="mb-3">
                            <label className="form-label fw-bold">Amount to Withdraw ($)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                className="form-control form-control-lg"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px", fontSize: "1.2rem" }}
                                required
                            />
                        </div>
                        <div className="text-center mt-3">
                            <button
                                type="submit"
                                className="button btn-lg btn-theme full-rounded animated right-icn"
                            >
                                <span>Submit Withdrawal <i className="glyph-icon flaticon-hearts" aria-hidden="true" /></span>
                            </button>
                        </div>
                    </form>
                ) : (stripeStatus?.isDetailsSubmitted && !stripeStatus?.isPayoutEnabled) ? (
                    <div className="text-center p-5 mt-4" style={{ background: "rgba(255, 255, 255, 0.05)", borderRadius: "20px", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(10px)" }}>
                        <div className="mb-4">
                            <i className="fa fa-clock-o text-warning" style={{ fontSize: "3rem" }}></i>
                        </div>
                        <h4 className="text-white fw-bold mb-3">Verification Pending</h4>
                        <p className="opacity-75" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                            Your details have been successfully submitted. <br />
                            Please wait <strong>24 hours</strong> for approval and account verification.
                        </p>
                    </div>
                ) : ((!stripeStatus?.isWithDrawable) && (stripeStatus?.isDetailsSubmitted && stripeStatus?.isPayoutEnabled)) ? (
                    <div className="text-center p-5 mt-4" style={{ background: "rgba(220, 53, 69, 0.08)", borderRadius: "20px", border: "1px solid rgba(220, 53, 69, 0.2)", backdropFilter: "blur(10px)" }}>
                        <div className="mb-4">
                            <i className="fa fa-exclamation-triangle text-danger" style={{ fontSize: "3rem" }}></i>
                        </div>
                        <h4 className="text-white fw-bold mb-3">Service Temporarily Unavailable</h4>
                        <p className="opacity-75" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                            Due to a technical issue, the service is temporarily unavailable. <br />
                            Please try again later, once the issue has been resolved.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(handleSetupAccount)} className="mt-4 text-start">
                        <h3 className="mb-4">Account Verification Details</h3>


                        {/* First Name & Last Name */}
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">First Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="First Name"
                                    {...register("individual.first_name")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.individual?.first_name && <p className="text-danger mt-1">{errors.individual.first_name.message}</p>}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Last Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Last Name"
                                    {...register("individual.last_name")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.individual?.last_name && <p className="text-danger mt-1">{errors.individual.last_name.message}</p>}
                            </div>
                        </div>

                        {/* Email & Phone */}
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Email Address</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Email Address"
                                    {...register("individual.email")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.individual?.email && <p className="text-danger mt-1">{errors.individual.email.message}</p>}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Phone Number</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="13322568356"
                                    {...register("individual.phone")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.individual?.phone && <p className="text-danger mt-1">{errors.individual.phone.message}</p>}
                            </div>
                        </div>

                        {/* Date of Birth (Day, Month, Year) */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Date of Birth</label>
                            <div className="row">
                                <div className="col-md-4 mb-2">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Day (DD)"
                                        {...register("individual.dob.day")}
                                        style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                    />
                                    {errors.individual?.dob?.day && <p className="text-danger mt-1">{errors.individual.dob.day.message}</p>}
                                </div>
                                <div className="col-md-4 mb-2">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Month (MM)"
                                        {...register("individual.dob.month")}
                                        style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                    />
                                    {errors.individual?.dob?.month && <p className="text-danger mt-1">{errors.individual.dob.month.message}</p>}
                                </div>
                                <div className="col-md-4 mb-2">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Year (YYYY)"
                                        {...register("individual.dob.year")}
                                        style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                    />
                                    {errors.individual?.dob?.year && <p className="text-danger mt-1">{errors.individual.dob.year.message}</p>}
                                </div>
                            </div>
                        </div>

                        {/* SSN Number */}
                        <div className="mb-4">
                            <label className="form-label fw-bold">SSN (Last 4 Digits)</label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="0000"
                                maxLength="4"
                                {...register("individual.ssn_last_4")}
                                style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                            />
                            {errors.individual?.ssn_last_4 && <p className="text-danger mt-1">{errors.individual.ssn_last_4.message}</p>}
                        </div>

                        {/* Address Line 1 */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Street Address</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Street address, P.O. box"
                                {...register("individual.address.line1")}
                                style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                            />
                            {errors.individual?.address?.line1 && <p className="text-danger mt-1">{errors.individual.address.line1.message}</p>}
                        </div>

                        {/* Country, State, City Row */}
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-bold">Country</label>
                                <CountrySelect
                                    onChange={(e) => {
                                        setCountryid(e.id);
                                        setValue('individual.address.country', e.iso2);
                                        trigger('individual.address.country');
                                    }}
                                    placeHolder="Select Country"
                                    inputClassName="form-control"
                                    defaultValue={{ id: 233, name: "United States", iso2: "US" }}
                                    disabled={true}
                                />
                                <input type="hidden" {...register("individual.address.country")} />
                                {errors.individual?.address?.country && <p className="text-danger mt-1">{errors.individual.address.country.message}</p>}
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-bold">State / Province</label>
                                <StateSelect
                                    countryid={countryid}
                                    onChange={(e) => {
                                        setStateid(e.id);
                                        setValue('individual.address.state', e.iso2);
                                        trigger('individual.address.state');
                                    }}
                                    placeHolder="Select State"
                                    inputClassName="form-control"
                                />
                                <input type="hidden" {...register("individual.address.state")} />
                                {errors.individual?.address?.state && <p className="text-danger mt-1">{errors.individual.address.state.message}</p>}
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-bold">City</label>
                                <CitySelect
                                    countryid={countryid}
                                    stateid={stateid}
                                    onChange={(e) => {
                                        setValue('individual.address.city', e.name);
                                        trigger('individual.address.city');
                                    }}
                                    placeHolder="Select City"
                                    inputClassName="form-control"
                                />
                                <input type="hidden" {...register("individual.address.city")} />
                                {errors.individual?.address?.city && <p className="text-danger mt-1">{errors.individual.address.city.message}</p>}
                            </div>
                        </div>

                        {/* Postal Code */}
                        <div className="row">
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-bold">Postal / ZIP Code</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="94111"
                                    {...register("individual.address.postal_code")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.individual?.address?.postal_code && <p className="text-danger mt-1">{errors.individual.address.postal_code.message}</p>}
                            </div>
                        </div>

                        {/* Bank Account Details Section */}
                        <h4 className="mt-4 mb-3 border-top pt-3 border-secondary text-center">Bank Account Payout Details</h4>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Account Holder Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="John Doe"
                                    {...register("account_holder_name")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.account_holder_name && <p className="text-danger mt-1">{errors.account_holder_name.message}</p>}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Routing Number</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="110000000"
                                    {...register("routing_number")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.routing_number && <p className="text-danger mt-1">{errors.routing_number.message}</p>}
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Account Number</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="000123456789"
                                    {...register("account_number")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.account_number && <p className="text-danger mt-1">{errors.account_number.message}</p>}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Confirm Account Number</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="000123456789"
                                    {...register("confirm_account_number")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errors.confirm_account_number && <p className="text-danger mt-1">{errors.confirm_account_number.message}</p>}
                            </div>
                        </div>

                        <div className="text-center mt-3">
                            <button
                                type="submit"
                                className="button btn-lg btn-theme full-rounded animated right-icn"
                            >
                                <span>Continue <i className="glyph-icon flaticon-hearts" aria-hidden="true" /></span>
                            </button>
                        </div>
                    </form >
                )
                }

                <div className="mt-5 pt-4 border-top border-secondary">
                    <h4 className="fw-bold mb-3 opacity-75">How it works?</h4>
                    <ol className="opacity-75 ps-3" style={{ listStyleType: "decimal", lineHeight: "1.8" }}>
                        <li>Connect your Stripe account.</li>
                        <li>Complete identity and bank account verification on Stripe onboarding page.</li>
                        <li>Request withdrawal of your earned balance directly to your bank account.</li>
                    </ol>
                </div>
            </div >
        </>
    );
}

export default memo(Withdrawal);
